import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { isRecomputeDone, useAdminBillingRecomputeJob, useRecomputeAdminBillingUsage } from '../../hooks/use-admin-billing'

/** utcDay is today (offset 0) or yesterday (-1) as a UTC YYYY-MM-DD date. */
function utcDay(offset: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() + offset)
  return d.toISOString().slice(0, 10)
}

/**
 * BillingRecomputeSection enqueues the usage count of today or yesterday (UTC)
 * for one organization (spec 2026-10-08-01), then follows the job until it
 * ends: queued, running, done or failed. A closed month is not changed.
 */
export function BillingRecomputeSection({ orgUid }: { orgUid: string }) {
  const { t } = useTranslation('admin')
  const recompute = useRecomputeAdminBillingUsage()
  const [job, setJob] = useState<{ uid: string; day: string } | null>(null)
  const [enqueueFailed, setEnqueueFailed] = useState(false)
  const { data: state } = useAdminBillingRecomputeJob(orgUid, job?.uid ?? null)

  const status = job ? (state?.status ?? 'queued') : null
  const inProgress = recompute.isPending || (status !== null && !isRecomputeDone(status))

  const run = (offset: number) => {
    setJob(null)
    setEnqueueFailed(false)
    recompute.mutate(
      { params: { path: { orgUid } }, body: { day: utcDay(offset) } },
      {
        onSuccess: (res) => setJob({ uid: res.job_uid, day: res.day }),
        onError: () => setEnqueueFailed(true),
      },
    )
  }

  let message: { tone: 'muted' | 'ok' | 'error'; text: string } | null = null
  if (enqueueFailed) {
    message = { tone: 'error', text: t('billing.recompute.error') }
  } else if (job && status) {
    const day = job.day
    switch (status) {
      case 'success':
        message = { tone: 'ok', text: t('billing.recompute.done', { day }) }
        break
      case 'failed':
        message = {
          tone: 'error',
          text: state?.error ? t('billing.recompute.failed_with', { day, error: state.error }) : t('billing.recompute.failed', { day }),
        }
        break
      case 'cancelled':
        message = { tone: 'error', text: t('billing.recompute.cancelled', { day }) }
        break
      case 'running':
        message = { tone: 'muted', text: t('billing.recompute.running', { day }) }
        break
      default:
        message = { tone: 'muted', text: t('billing.recompute.queued', { day }) }
    }
  }

  const toneClass = { muted: 'text-muted-foreground', ok: 'text-success', error: 'text-destructive' }

  return (
    <section className="space-y-3" data-testid="billing-recompute">
      <div>
        <h2 className="text-lg font-semibold">{t('billing.recompute.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('billing.recompute.help')}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => run(0)} disabled={inProgress} data-testid="billing-recompute-today">
          {t('billing.recompute.today')}
        </Button>
        <Button variant="outline" onClick={() => run(-1)} disabled={inProgress} data-testid="billing-recompute-yesterday">
          {t('billing.recompute.yesterday')}
        </Button>
        {message && (
          <span
            className={`inline-flex items-center text-sm ${toneClass[message.tone]}`}
            data-testid="billing-recompute-message"
            data-status={enqueueFailed ? 'error' : status ?? undefined}
          >
            {inProgress && <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />}
            {message.text}
          </span>
        )}
      </div>
    </section>
  )
}
